import { createSelector } from 'reselect';
import type { RootState } from 'typesafe-actions';

import type { GetTranslation } from '@travel/translation';

import type { Plan } from 'ProviderPlanList-Types';

import {
  getBedDetails,
  getMaxAdultChild,
  getRoomSizeText,
} from '../../pages/ProviderInformationPage/helpers';
import type { ProviderPlanListState } from './reducer';

export const getProviderRatePlan = (state: RootState) => state.providerPlanList;

export const getIsFetching = createSelector(
  getProviderRatePlan,
  (state: ProviderPlanListState) => state.isFetching,
);

export const getPlansTotal = createSelector(
  getProviderRatePlan,
  (state: ProviderPlanListState) => state.items?.length || 0,
);

export const getPlans = createSelector(
  getProviderRatePlan,
  (state: ProviderPlanListState) => state.items,
);

export const getFilters = createSelector(
  getProviderRatePlan,
  (state: ProviderPlanListState) => state.filters,
);

export const getSelectedFeatures = createSelector(
  getProviderRatePlan,
  (state: ProviderPlanListState) => state.selectedFeatures,
);

export const getErrors = createSelector(
  getProviderRatePlan,
  (state: ProviderPlanListState) => state.errors,
);

export const getRoomDetails = (
  roomId: string,
  planId: string,
  getTranslation: GetTranslation,
  isDirectContractType: boolean,
) =>
  createSelector(getPlans, (plans: Array<Plan>) => {
    const plan = plans.find((plan) => plan.planId === planId);
    if (!plan) {
      return {};
    }

    const room = plan.items.find((room) => room.id === roomId);
    if (!room) {
      return {};
    }

    return {
      ...room,
      capacityText: getMaxAdultChild(room.capacity, getTranslation),
      bedText: getBedDetails({
        beds: room.bedSizes,
        isIndirectProvider: !isDirectContractType,
        shouldTranslate: false,
        getTranslation,
      }),
      roomText: getRoomSizeText(room.roomSize),
    };
  });

export const getPlanDetails = (id: string) =>
  createSelector(getPlans, (plans: Array<Plan>) => {
    const plan = plans.find((plan) => plan.planId === id);
    if (!plan) {
      return {};
    }

    // lowest plan price
    const lowestPlanPrice = Math.min.apply(
      null,
      plan.items.map((plan) => plan.price.total),
    );

    return {
      ...plan,
      lowestPlanPrice: lowestPlanPrice,
      displayImage: plan.media?.find((item) => item.url || item.videoVendorId),
    };
  });

export const getSalesPromotions = createSelector(
  getProviderRatePlan,
  (state: ProviderPlanListState) => {
    const itemPlans = state.items;
    return itemPlans
      .map((itemPlan) =>
        itemPlan.items
          .map((plan) => plan?.price?.salesPromotions)
          .flat()
          .filter((sp) => sp)
          .map((sp) => sp.id)
          .filter((spId) => spId)
          .join(','),
      )
      .filter((spIds) => spIds)
      .join(',');
  },
);
